import cx from "classnames";
import { faAngleRight, faBell, faBellSlash, faPaw } from "@fortawesome/pro-regular-svg-icons";

import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { Optional } from "common/utils/types";

import TextChip, { TextChipProps } from "client/common/components/content/TextChip";
import Box from "client/common/components/layouts/Box";

import { DosageReminder, DosageReminderPatientItem } from "../types";
import { SPECIES_ICONS } from "../constants";
import { getFrequencyLabel } from "../utils";

import styles from "../styles/CreateItemCard.module.less";

const getReminderStatusLabel = (reminder: Optional<DosageReminder>) => {
  if (!reminder) {
    return null;
  }
  if (reminder.is_active) {
    return getFrequencyLabel(reminder);
  }
  return "Inactive";
};

type CreateItemCardProps = {
  patientItem: DosageReminderPatientItem;
  reminder: Optional<DosageReminder>;
  patientChipVariant: TextChipProps["variant"];
  onClick: () => void;
};

const CreateItemCard = ({
  patientItem,
  reminder,
  patientChipVariant,
  onClick,
}: CreateItemCardProps) => {
  const { patient, item } = patientItem;
  const productName = item.product.product_line.name;
  const statusLabel = getReminderStatusLabel(reminder);

  return (
    <LinkButton onClick={onClick}>
      <Box
        className={cx(
          styles.card,
          reminder && (reminder.is_active ? styles.existingReminder : styles.inactive),
        )}
      >
        <CatalogImage
          image={item.image}
          size="thumbnail"
          alt={productName}
          className={styles.image}
        />
        <div className={styles.details}>
          <span className={styles.name}>{productName}</span>
          <span className={styles.variant}>
            {item.product.name} · {item.name}
          </span>
          <div className={styles.patient}>
            <TextChip variant={patientChipVariant} className={styles.patientChip}>
              <Icon icon={SPECIES_ICONS[patient.species] ?? faPaw} className={styles.speciesIcon} />
              <span className={styles.patientName}>{patient.name}</span>
            </TextChip>
            {statusLabel && <span className={styles.status}>{statusLabel}</span>}
          </div>
        </div>
        <div className={styles.actions}>
          {reminder && (
            <Icon
              icon={reminder.is_active ? faBell : faBellSlash}
              title={reminder.is_active ? "Reminder on" : "Reminder off"}
              className={styles.reminderIcon}
            />
          )}
          <Icon icon={faAngleRight} className={styles.chevron} />
        </div>
      </Box>
    </LinkButton>
  );
};

export default CreateItemCard;
